import { useEffect, useState } from 'react';
import {
  useCreateMenuAliasMutation,
  useCreateMenuItemMutation,
  useDeleteMenuItemMutation,
  useGetLocationsQuery,
  useGetMenuQuery,
  useSetItemAvailabilityMutation,
  useTestMenuMatchMutation,
  useUpdateMenuItemMutation,
  type MenuItemRecord
} from '@/store/api';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { Button, EmptyState, Field, Panel, SkeletonRows } from '@/components/ui';
import { PosStatusBanner } from '@/features/integrations/PosStatusBanner';
import { CategoriesPanel, ModifierGroupsPanel, SourceTag } from './MenuStructure';
import { useGetPosStatusQuery } from '@/store/accountApi';
import { formatMoney } from '@/utils/format';

/**
 * The alias tester is the important part of this screen: it shows the operator
 * exactly what the phone line would do with a spoken phrase, so mismatches get
 * fixed before a customer hits them.
 */
export function MenuPage() {
  const dispatch = useAppDispatch();
  const locations = useGetLocationsQuery();
  const [locationId, setLocationId] = useState('');

  useEffect(() => {
    const first = locations.data?.data[0]?._id;
    if (first && !locationId) setLocationId(first);
  }, [locations.data, locationId]);

  const menu = useGetMenuQuery(locationId, { skip: !locationId });
  const [setAvailability] = useSetItemAvailabilityMutation();
  const [testPhrase, { data: testResult, isLoading: testing }] = useTestMenuMatchMutation();
  const [phrase, setPhrase] = useState('');

  // Drives the "POS managed" labels, so an owner can see at a glance which
  // rows they can edit here and which belong to the till.
  const { data: posStatus } = useGetPosStatusQuery();
  const posProvider = posStatus?.data.find((row) => row.locationId === locationId)?.provider;

  const [createItem, { isLoading: creating }] = useCreateMenuItemMutation();
  const [updateItem] = useUpdateMenuItemMutation();
  const [deleteItem] = useDeleteMenuItemMutation();
  const [createAlias] = useCreateMenuAliasMutation();

  /** The item currently open in the editor, or 'new' for a fresh one. */
  const [editing, setEditing] = useState<MenuItemRecord | 'new' | null>(null);

  const saveItem = async (draft: Partial<MenuItemRecord>) => {
    const result =
      editing === 'new'
        ? await createItem({ locationId, ...draft })
        : await updateItem({ id: (editing as MenuItemRecord)._id, ...draft });

    if ('error' in result) {
      dispatch(pushToast('Could not save that item.', 'error'));
      return;
    }

    dispatch(pushToast(editing === 'new' ? 'Item added.' : 'Item saved.', 'success'));
    setEditing(null);
  };

  const removeItem = async (item: MenuItemRecord) => {
    const result = await deleteItem(item._id);
    dispatch(
      'error' in result
        ? pushToast('Could not remove that item.', 'error')
        : pushToast(`${item.name} removed from the menu.`, 'success')
    );
  };

  const addAlias = async (menuItemId: string, phrase: string) => {
    const result = await createAlias({ locationId, menuItemId, phrase });
    dispatch(
      'error' in result
        ? pushToast('Could not add that name.', 'error')
        : pushToast('Name added. Callers can now ask for it that way.', 'success')
    );
  };

  const toggle = async (id: string, available: boolean) => {
    const result = await setAvailability({ id, available });
    dispatch(
      'error' in result
        ? pushToast('Could not update that item.', 'error')
        : pushToast(available ? 'Item is back on the menu.' : 'Item marked unavailable.', 'success')
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-display">Menu</h1>
        {(locations.data?.data.length ?? 0) > 1 ? (
          <select
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
            className="rounded border border-rule bg-white px-2 py-1.5 text-small"
            aria-label="Location"
          >
            {locations.data?.data.map((location) => (
              <option key={location._id} value={location._id}>
                {location.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      <PosStatusBanner locationId={locationId} />

      <Panel title="Try a phrase">
        <div className="px-4 py-4">
          <p className="text-small text-slate">
            Say something the way a customer would. This shows what the phone line hears,
            without placing an order.
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            <input
              value={phrase}
              onChange={(event) => setPhrase(event.target.value)}
              placeholder="two masala dosas and a mango lassi"
              className="min-w-[16rem] flex-1 rounded border border-rule bg-white px-3 py-2 text-small"
              aria-label="Phrase to test"
            />
            <Button
              loading={testing}
              disabled={!phrase || !locationId}
              onClick={() => testPhrase({ phrase, locationId })}
            >
              Test phrase
            </Button>
          </div>

          {testResult ? (
            testResult.matches.length === 0 ? (
              <p className="mt-4 rounded bg-amber-soft px-3 py-2 text-small text-amber">
                Nothing on the menu matches this. The caller would hear the fallback and be
                offered a person. Add an alias to fix it.
              </p>
            ) : (
              <ul className="mt-4 divide-y divide-rule border-t border-rule">
                {testResult.matches.map((match) => (
                  <li key={match.name} className="flex items-center gap-3 py-2.5">
                    <span className="tabular text-small text-slate">×{match.quantity}</span>
                    <span className="text-small text-ink">{match.name}</span>
                    <span className="tabular ml-auto text-micro text-slate">
                      {Math.round(match.confidence * 100)}%
                    </span>
                    <span
                      className={`rounded-sm px-2 py-0.5 text-micro ${
                        match.wouldAdd
                          ? 'bg-jade-soft text-jade'
                          : match.wouldConfirm
                            ? 'bg-amber-soft text-amber'
                            : 'bg-rust-soft text-rust'
                      }`}
                    >
                      {match.wouldAdd
                        ? 'Added straight away'
                        : match.wouldConfirm
                          ? 'Read back to confirm'
                          : 'Not confident enough'}
                    </span>
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </div>
      </Panel>

      <CategoriesPanel
        locationId={locationId}
        categories={menu.data?.categories ?? []}
        provider={posProvider}
      />

      <Panel
        title="Items"
        action={
          <Button onClick={() => setEditing('new')} disabled={!locationId}>
            Add item
          </Button>
        }
      >
        {editing ? (
          <MenuItemEditor
            item={editing === 'new' ? null : editing}
            saving={creating}
            onCancel={() => setEditing(null)}
            onSave={saveItem}
          />
        ) : null}

        {menu.isLoading ? (
          <SkeletonRows rows={8} />
        ) : (menu.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            headline="No menu items yet"
            hint="Connect your POS to bring your menu across, or add items by hand."
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-rule text-left text-micro text-slate">
                <th className="px-4 py-2 font-medium">Item</th>
                <th className="px-4 py-2 font-medium">Also called</th>
                <th className="px-4 py-2 font-medium">Price</th>
                <th className="px-4 py-2 font-medium">Available</th>
                <th className="px-4 py-2 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {menu.data?.items.map((item) => (
                <tr key={item._id} className="hover:bg-paper">
                  <td className="px-4 py-3 text-small text-ink">
                    <span className="mr-2">{item.name}</span>
                    <SourceTag externalPosId={item.externalPosId} provider={posProvider} />
                  </td>
                  <td className="px-4 py-3 text-small text-slate">
                    {item.aliases.length > 0 ? item.aliases.join(', ') : '—'}
                  </td>
                  <td className="tabular px-4 py-3 text-small text-ink">
                    {formatMoney(item.priceCents)}
                  </td>
                  <td className="px-4 py-3">
                    <label className="flex items-center gap-2 text-small text-slate">
                      <input
                        type="checkbox"
                        checked={item.available}
                        onChange={(event) => toggle(item._id, event.target.checked)}
                        className="h-4 w-4 accent-[#0B7A5A]"
                      />
                      {item.available ? 'On' : 'Off'}
                    </label>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setEditing(item)}
                        className="text-small text-jade underline underline-offset-2"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const phrase = window.prompt(
                            `What else do callers call the ${item.name}?`
                          );
                          if (phrase?.trim()) addAlias(item._id, phrase.trim());
                        }}
                        className="text-small text-slate underline underline-offset-2"
                      >
                        Add name
                      </button>
                      {item.externalPosId ? null : (
                        <button
                          type="button"
                          onClick={() => removeItem(item)}
                          className="text-small text-rust underline underline-offset-2"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}


/**
 * Add or edit one menu item.
 *
 * Price is entered in dollars and stored in cents: the phone line, the POS and
 * the payment link all work in minor units, and a rounding error here becomes a
 * wrong charge on someone's card.
 */
function MenuItemEditor({
  item,
  saving,
  onSave,
  onCancel
}: {
  item: MenuItemRecord | null;
  saving: boolean;
  onSave: (draft: Partial<MenuItemRecord>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(item?.name ?? '');
  const [price, setPrice] = useState(String((item?.priceCents ?? 0) / 100));
  const [description, setDescription] = useState(item?.description ?? '');
  const [aliases, setAliases] = useState((item?.aliases ?? []).join(', '));
  const [vegetarian, setVegetarian] = useState(Boolean(item?.vegetarian));
  const [available, setAvailable] = useState(item?.available ?? true);

  const submit = () => {
    if (!name.trim()) return;

    onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      priceCents: Math.round(Number(price) * 100),
      aliases: aliases
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean),
      vegetarian,
      available
    });
  };

  return (
    <div className="border-b border-rule bg-paper px-4 py-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name">
          <input className="input" value={name} onChange={(event) => setName(event.target.value)} />
        </Field>

        <Field label="Price">
          <input
            type="number"
            step="0.01"
            min="0"
            className="input tabular"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
          />
        </Field>

        <Field label="Description">
          <input
            className="input"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <Field label="Also called">
          <input
            className="input"
            placeholder="dosa, masala dose"
            value={aliases}
            onChange={(event) => setAliases(event.target.value)}
          />
        </Field>
      </div>

      <p className="mt-1.5 text-micro text-slate">
        Separate alternative names with commas. These are what the phone line listens for, so add
        whatever your regulars actually say.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-small text-slate">
          <input
            type="checkbox"
            checked={available}
            onChange={(event) => setAvailable(event.target.checked)}
            className="h-4 w-4 accent-[#0B7A5A]"
          />
          Available
        </label>

        <label className="flex items-center gap-2 text-small text-slate">
          <input
            type="checkbox"
            checked={vegetarian}
            onChange={(event) => setVegetarian(event.target.checked)}
            className="h-4 w-4 accent-[#0B7A5A]"
          />
          Vegetarian
        </label>

        <div className="ml-auto flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="text-small text-slate underline underline-offset-2"
          >
            Cancel
          </button>
          <Button onClick={submit} loading={saving} disabled={!name.trim()}>
            {item ? 'Save item' : 'Add item'}
          </Button>
        </div>
      </div>
    </div>
  );
}
